import {
  formatColorPickerColorHex,
  SearchInput,
  Text,
  type SearchOption,
} from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import type { Label } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatTextModal } from "@/components/modal";
import { HatLabelChip } from "./HatLabelChip";
import { deriveLabelSuggestions } from "./labelSuggestions";
import { normalizeLabelText } from "./labelText";
import { genericLabelKeys, newLabelBlocker, type NewLabelBlocker } from "./newLabelDraft";
import type { LabelCatalogStatus } from "./useLabelCatalog";
import styles from "./NewLabelDialog.module.css";

export type NewLabelDialogProps = {
  open: boolean;
  busy: boolean;
  // 제안과 중복 판정에 쓰는 라벨 카탈로그. 호출자가 이미 적재한 목록을 그대로 받는다.
  catalog: readonly Label[];
  // 그 카탈로그의 적재 상태. 다 보기 전에는 "없다" 고 단정할 수 없고, 중복도 판정할 수 없다.
  catalogStatus: LabelCatalogStatus;
  // 여기서 만들 수 없는 key. 하드웨어 축은 모듈이 아는 고정 목록이라 여기 없어도 막는다.
  blockedKeys: readonly string[];
  onCreate: (key: string, value: string) => Promise<void>;
  onClose: () => void;
};

/**
 * 새 라벨 (key, value) 만들기 창.
 *
 * 목록 옆 인라인 입력 대신 창을 쓴다. "이미 있는 것을 고르기" 와 "없는 것을 새로 만들기" 는
 * 무게가 다른 일이기 때문이다. 새로 만든 라벨은 카탈로그에 남아 다른 데이터셋에도 표시된다.
 *
 * key 와 값은 둘 다 자동완성 입력이다.
 * - key 후보는 카탈로그의 generic key 뿐이다. 그래서 사용자는 예약·하드웨어 축을 우연히 고를 수
 *   없다. 직접 입력하면 어느 화면에서 입력해야 하는지 사유를 표시한다.
 * - 값 후보는 고른 key 의 기존 값이다. 그래서 사용자는 같은 뜻의 다른 철자를 입력 중에 알아챈다.
 *
 * **카탈로그를 다 보기 전에는 만들지 않는다.** 목록이 비어 있으면 중복 판정이 통과한다. 그러면
 * 같은 뜻의 라벨을 막는 이 창의 목적이 경고 없이 무효가 된다.
 *
 * Owns: 입력 상태·미리보기·막는 사유의 표시.
 * Does not own: 판단(`newLabelDraft`) · 카탈로그 적재 · 생성 요청(호출자의 `onCreate`).
 */
export function NewLabelDialog({
  open,
  busy,
  catalog,
  catalogStatus,
  blockedKeys,
  onCreate,
  onClose,
}: NewLabelDialogProps): ReactNode {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const catalogReady = catalogStatus === "ready";

  const keyOptions: SearchOption[] = genericLabelKeys(catalog, blockedKeys).map((entry) => ({
    value: entry,
    label: entry,
  }));
  // 값 후보는 고른 key 의 기존 값이다. 색과 설명도 함께 표시해 같은 뜻의 값을 또 만들지 않게 한다.
  const valueOptions: SearchOption[] = deriveLabelSuggestions(catalog, key.trim()).map(
    (suggestion) => ({
      value: suggestion.value,
      label: suggestion.value,
      description: suggestion.description ?? undefined,
      accentColor: formatColorPickerColorHex(suggestion.color),
    }),
  );

  // 적재가 끝나기 전에는 어떤 입력으로도 만들 수 없다. 이 사유가 차단 사유보다 먼저다.
  const blocker: NewLabelBlocker | null = catalogReady
    ? newLabelBlocker(key, value, catalog, blockedKeys)
    : { field: "key", message: catalogGateMessage(catalogStatus) };
  // 아직 입력하지 않은 칸에 "입력해 주세요" 를 표시하면 불필요한 경고가 된다. 사용자가 입력한
  // 칸만 사유를 표시한다. 적재 게이트는 입력과 관계없는 조건이라 처음부터 표시한다.
  const showBlocker =
    blocker !== null &&
    (!catalogReady ||
      (blocker.field === "key" ? key !== "" : value !== "" || normalizeLabelText(key) !== ""));

  function reset(): void {
    setKey("");
    setValue("");
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  function handleCreate(): void {
    if (blocker !== null || busy) {
      return;
    }
    void onCreate(key.trim(), value.trim()).then(() => {
      reset();
      onClose();
    });
  }

  return (
    <HatTextModal
      open={open}
      label="새 라벨 만들기"
      caption="여기서 만든 라벨은 카탈로그에 남아 다른 Dataset에서도 고를 수 있습니다."
      okText="만들기"
      cancelText="취소"
      okDisabled={blocker !== null}
      busy={busy}
      onOkPress={handleCreate}
      onCancelPress={handleClose}
      content={
        <div className={styles.form}>
          <div className={styles.field}>
            <Text variant={hatcheryTextModes["caption-bold"]}>키</Text>
            <SearchInput
              inputSize="sm"
              value={key}
              options={keyOptions}
              placeholder="key를 입력하거나 기존 key에서 골라 주세요."
              aria-label="라벨 key"
              disabled={busy}
              emptyMessage={
                catalogReady
                  ? "일치하는 기존 key가 없습니다. 그대로 새 key로 만듭니다."
                  : catalogGateMessage(catalogStatus)
              }
              onValueChange={setKey}
            />
          </div>

          <div className={styles.field}>
            <Text variant={hatcheryTextModes["caption-bold"]}>값</Text>
            <SearchInput
              inputSize="sm"
              value={value}
              options={valueOptions}
              placeholder="값을 입력하거나 이 key의 기존 값에서 골라 주세요."
              aria-label="라벨 값"
              disabled={busy}
              emptyMessage={
                catalogReady
                  ? "이 key에 쓰인 기존 값이 없습니다. 그대로 새 값으로 만듭니다."
                  : catalogGateMessage(catalogStatus)
              }
              onValueChange={setValue}
            />
          </div>

          <div className={styles.field}>
            <Text variant={hatcheryTextModes["caption-bold"]}>미리보기</Text>
            {blocker === null ? (
              <span>
                <HatLabelChip labelKey={key.trim()} value={value.trim()} color={0} size="xs" />
              </span>
            ) : (
              <Text variant={hatcheryTextModes["caption-normal"]} className={styles.muted}>
                key와 값을 채우면 만들어질 라벨이 여기 보입니다.
              </Text>
            )}
          </div>

          {showBlocker && blocker !== null ? (
            <Text
              variant={hatcheryTextModes["caption-normal"]}
              className={styles.warn}
              role="alert"
            >
              {blocker.message}
            </Text>
          ) : null}
        </div>
      }
    />
  );
}

// 적재가 끝나지 않았을 때 만들기를 막는 사유. 적재 실패는 "없다" 가 아니라 "확인하지 못했다" 다.
// 카탈로그를 보지 않고 새 라벨을 만들면, 같은 뜻의 라벨이 하나 더 생겨도 알 수 없다.
function catalogGateMessage(status: LabelCatalogStatus): string {
  return status === "failed"
    ? "기존 라벨을 확인하지 못해 새로 만들 수 없습니다. 잠시 후 다시 시도해 주세요."
    : "기존 라벨을 확인하고 있습니다.";
}
